import React, { useEffect, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ProductRow } from "../../../../services/v2/inventory.service";
import { ui, useDebounced } from "../kits/shared";
import { isTouchScreen } from "../../../../components/kits/kitUi";
import { VariantChips } from "./variantes";

// iOS zooms the whole page when a focused field has less than 16 px: touch screens get 16 px.
const inputStyle: React.CSSProperties = { ...ui.input, ...(isTouchScreen() ? { fontSize: "16px" } : {}) };

export interface ProductPickerProps {
	campusId: number;
	kinds?: string[];
	excludeIds?: number[];
	placeholder?: string;
	disabled?: boolean;
	/** Stock counts are blind: whoever counts must not see the system's stock. */
	hideStock?: boolean;
	/** Armar kits y uniformes: una fila por producto con todos sus colores y tallas, no una fila por talla. */
	groupVariants?: boolean;
	onPick: (product: ProductRow) => void;
}

/**
 * Buscador de productos para armar kits y para elegir sustitutos. Escribe y elige; también acepta el
 * texto del lector de código de barras, porque busca por código WA, SKU y código de barras.
 */
const ProductPicker: React.FC<ProductPickerProps> = ({ campusId, kinds, excludeIds, placeholder, disabled, hideStock, groupVariants, onPick }) => {
	const [query, setQuery] = useState<string>("");
	const debounced = useDebounced(query, 350);
	const [results, setResults] = useState<ProductRow[]>([]);
	const [open, setOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [failed, setFailed] = useState<boolean>(false);

	useEffect(() => {
		if (debounced.trim().length < 2) { setResults([]); return; }
		let alive = true;
		setLoading(true);
		setFailed(false);
		HttpManager.getInstance().inventoryService
			.list(campusId, { q: debounced.trim(), kinds: kinds || ["article", "vitrina", "uniform"], ...(groupVariants ? { groupVariants: 1 } : { includeVariants: 1 }) }, 1, 25)
			.then((response) => { if (alive) setResults(response.data.data || []); })
			// A failed search is not "no results": that made people create duplicates.
			.catch(() => { if (alive) { setResults([]); setFailed(true); } })
			.finally(() => { if (alive) setLoading(false); });
		return () => { alive = false; };
	}, [debounced, campusId, groupVariants]);

	const visible = results.filter((product) => !(excludeIds || []).includes(product.id));

	return <div style={{ position: "relative" }}>
		<input type="text" className="form-control" style={inputStyle} disabled={disabled} value={query}
			placeholder={placeholder || "Buscar producto por nombre, código o escanear…"}
			onFocus={() => setOpen(true)}
			onBlur={() => window.setTimeout(() => setOpen(false), 180)}
			onChange={(event) => { setQuery(event.target.value); setOpen(true); }} />
		{open && query.trim().length >= 2 ? <div style={{ position: "absolute", top: "38px", left: 0, right: 0, zIndex: 1600, background: "var(--wa-superficie)", border: "1px solid var(--wa-filo-2)", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)", maxHeight: "260px", overflowY: "auto" }}>
			{loading ? <div style={{ padding: "10px", color: "var(--wa-apagado)", fontSize: "13px" }}>Buscando…</div>
				: failed ? <div style={{ padding: "10px", color: "var(--wa-oro)", fontSize: "13px" }}>Sin señal: no se pudo buscar. Vuelve a intentar.</div>
				: visible.length < 1 ? <div style={{ padding: "10px", color: "var(--wa-apagado)", fontSize: "13px" }}>Sin resultados. Prueba con una sola palabra.</div>
				: visible.map((product) => <div key={product.id} onMouseDown={() => { onPick(product); setQuery(""); setResults([]); }}
					style={{ padding: "7px 10px", cursor: "pointer", borderBottom: "1px solid var(--wa-papel-2)", fontSize: "13px" }}>
					<div style={{ fontWeight: 600 }}>{product.name}</div>
					{groupVariants && product.variantSummary
						? <><div style={ui.tiny}>{product.waCode} · {product.kindLabel}{hideStock ? "" : " · existencia " + product.onHand}</div>
							<div style={{ marginTop: "3px" }}><VariantChips summary={product.variantSummary} /></div></>
						: <div style={ui.tiny}>{product.waCode}{product.colorValue ? " · " + product.colorValue : ""}{product.sizeValue ? " · talla " + product.sizeValue : ""} · {product.kindLabel}{hideStock ? "" : product.trackStock === false ? (product.variantCount > 0 ? " · " + product.variantCount + " variantes" : " · no se cuenta") : " · existencia " + product.onHand}</div>}
				</div>)}
		</div> : null}
	</div>;
};

export default ProductPicker;
